import React, { useState } from 'react';
import { Shield, CheckCircle, XCircle, AlertTriangle, FileText, Download } from 'lucide-react';
import { useAppContext } from '../../contexts/AppContext';
import { checkCompliance, generateComplianceReport, ComplianceReport, ComplianceIssue } from '../../utils/complianceChecker';

export const ComplianceChecker: React.FC = () => {
    const { allData } = useAppContext();
    const [report, setReport] = useState<ComplianceReport | null>(null);
    const [isChecking, setIsChecking] = useState(false);

    const runComplianceCheck = () => {
        setIsChecking(true);

        // Simulate slight delay for UX
        setTimeout(() => {
            const complianceReport = checkCompliance(allData);
            setReport(complianceReport);
            setIsChecking(false);
        }, 500);
    };

    const downloadReport = () => {
        if (!report) return;

        const markdown = generateComplianceReport(report);
        const blob = new Blob([markdown], { type: 'text/markdown' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `compliance-report-${new Date().toISOString().split('T')[0]}.md`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const getScoreColor = (score: number) => {
        if (score >= 90) return 'text-green-600 dark:text-green-400';
        if (score >= 70) return 'text-yellow-600 dark:text-yellow-400';
        return 'text-red-600 dark:text-red-400';
    };

    const getScoreBgColor = (score: number) => {
        if (score >= 90) return 'bg-green-100 dark:bg-green-900/20';
        if (score >= 70) return 'bg-yellow-100 dark:bg-yellow-900/20';
        return 'bg-red-100 dark:bg-red-900/20';
    };

    const groupIssuesByCategory = (issues: ComplianceIssue[]) => {
        return issues.reduce((acc, issue) => {
            if (!acc[issue.category]) acc[issue.category] = [];
            acc[issue.category].push(issue);
            return acc;
        }, {} as Record<string, ComplianceIssue[]>);
    };

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <div className="mb-8">
                <div className="flex items-center gap-3 mb-2">
                    <div className="bg-indigo-600 p-2 rounded-lg">
                        <Shield className="text-white" size={24} />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                            Metadata Standards Compliance Checker
                        </h1>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            Validate project data against Metadata Standards v2.0.0
                        </p>
                    </div>
                </div>
            </div>

            {/* Action Button */}
            <div className="mb-6">
                <button
                    onClick={runComplianceCheck}
                    disabled={isChecking}
                    className="flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white rounded-lg font-semibold transition-colors shadow-sm"
                >
                    {isChecking ? (
                        <>
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Running Compliance Check...
                        </>
                    ) : (
                        <>
                            <Shield size={20} />
                            Run Compliance Check
                        </>
                    )}
                </button>
            </div>

            {/* Report Display */}
            {report && (
                <div className="space-y-6">
                    {/* Summary Card */}
                    <div className={`rounded-xl p-8 ${getScoreBgColor(report.score)}`}>
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                                    Compliance Score
                                </h2>
                                <p className="text-gray-600 dark:text-gray-400">
                                    {report.isCompliant ? '✅ PASS - Fully Compliant' : '❌ FAIL - Issues Found'}
                                </p>
                            </div>
                            <div className={`text-6xl font-bold ${getScoreColor(report.score)}`}>
                                {report.score}%
                            </div>
                        </div>

                        <div className="grid grid-cols-4 gap-4">
                            <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                                <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Checks</div>
                                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                                    {report.summary.totalChecks}
                                </div>
                            </div>
                            <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                                <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Passed</div>
                                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                                    {report.summary.passed}
                                </div>
                            </div>
                            <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                                <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Failed</div>
                                <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                                    {report.summary.failed}
                                </div>
                            </div>
                            <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                                <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Warnings</div>
                                <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                                    {report.summary.warnings}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Issues List */}
                    {report.issues.length > 0 ? (
                        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                                    Issues Found ({report.issues.length})
                                </h3>
                                <button
                                    onClick={downloadReport}
                                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-lg transition-colors"
                                >
                                    <Download size={16} />
                                    Download Report
                                </button>
                            </div>

                            <div className="p-6 space-y-6">
                                {Object.entries(groupIssuesByCategory(report.issues)).map(([category, issues]) => (
                                    <div key={category}>
                                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 capitalize">
                                            {category.replace(/([A-Z])/g, ' $1').trim()}
                                        </h4>
                                        <div className="space-y-2">
                                            {issues.map((issue, idx) => (
                                                <div
                                                    key={idx}
                                                    className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg border-l-4"
                                                    style={{
                                                        borderLeftColor: issue.severity === 'error' ? '#ef4444' : '#f59e0b'
                                                    }}
                                                >
                                                    {issue.severity === 'error' ? (
                                                        <XCircle className="text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" size={20} />
                                                    ) : (
                                                        <AlertTriangle className="text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" size={20} />
                                                    )}
                                                    <div className="flex-1">
                                                        <div className="font-semibold text-gray-900 dark:text-white mb-1">
                                                            {issue.field}
                                                        </div>
                                                        <div className="text-sm text-gray-600 dark:text-gray-400">
                                                            {issue.message}
                                                        </div>
                                                        {issue.location && (
                                                            <div className="text-xs text-gray-500 dark:text-gray-500 mt-1 font-mono">
                                                                Location: {issue.location}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-8 text-center border-2 border-green-200 dark:border-green-800">
                            <CheckCircle className="mx-auto text-green-600 dark:text-green-400 mb-4" size={48} />
                            <h3 className="text-xl font-bold text-green-900 dark:text-green-100 mb-2">
                                ✅ Fully Compliant!
                            </h3>
                            <p className="text-green-700 dark:text-green-300">
                                No issues found. This project fully complies with Metadata Standards v2.0.0
                            </p>
                        </div>
                    )}

                    {/* Documentation Link */}
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                        <div className="flex items-start gap-3">
                            <FileText className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" size={20} />
                            <div>
                                <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-1">
                                    Documentation
                                </h4>
                                <p className="text-sm text-blue-700 dark:text-blue-300 mb-2">
                                    For more information about metadata standards and how to fix compliance issues:
                                </p>
                                <ul className="text-sm text-blue-600 dark:text-blue-400 space-y-1">
                                    <li>• <a href="file:///c:/Users/wayne/Desktop/Living Reliability Systems/apps/Magnus-Core/docs/METADATA_STANDARDS.md" target="_blank" rel="noreferrer" className="underline">Metadata Standards v2.0.0</a></li>
                                    <li>• <a href="file:///c:/Users/wayne/Desktop/Living Reliability Systems/apps/Magnus-Core/docs/METADATA_COMPLIANCE_AUDIT.md" target="_blank" rel="noreferrer" className="underline">Compliance Audit Report</a></li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Initial State */}
            {!report && !isChecking && (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-12 text-center border-2 border-dashed border-gray-300 dark:border-gray-700">
                    <Shield className="mx-auto text-gray-400 dark:text-gray-600 mb-4" size={64} />
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                        Ready to Check Compliance
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                        Click &quot;Run Compliance Check&quot; to validate your project data against Metadata Standards v2.0.0
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-500">
                        This will check all FMEA items, BOM items, P-Diagrams, DVP&R tests, and project metadata
                    </p>
                </div>
            )}
        </div>
    );
};

export default ComplianceChecker;
